import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './EmailBodyPreview.css';

interface EmailBodyPreviewProps {
  html: string;
  loading?: boolean;
  /**
   * Высота iframe по содержимому, не выше contentMaxVh от высоты окна.
   * До первого измерения показывается высота по умолчанию (достаточная, чтобы письмо не схлопывалось).
   */
  fitHeightToContent?: boolean;
  contentMaxVh?: number;
}

const EMPTY_PLACEHOLDER =
  '<p style="margin:0;font:14px/1.45 system-ui,sans-serif;color:#888">Здесь появится текст письма</p>';

function stripScriptsFromHtmlForPreview(html: string): string {
  return html
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<script\b[^>]*\/?>/gi, '');
}

function buildPreviewSrcDoc(bodyHtml: string): string {
  const css = `
    html, body {
      margin: 0;
      overflow: auto;
      scrollbar-width: none;
      -ms-overflow-style: none;
      box-sizing: border-box;
    }
    body {
      padding: 0;
    }
    html::-webkit-scrollbar,
    body::-webkit-scrollbar {
      display: none;
      width: 0 !important;
      height: 0 !important;
    }
  `.trim();
  return `<!DOCTYPE html><html lang="ru"><head><meta charset="utf-8"><style>${css}</style></head><body>${bodyHtml}</body></html>`;
}

const FIT_MIN_PX = 240;
/** Потолок области предпросмотра: не выше 700px и не выше contentMaxVh% окна */
const PREVIEW_MAX_HEIGHT_PX = 700;

function previewHeightCapPx(contentMaxVh: number): number {
  const vhCap = Math.floor((window.innerHeight * contentMaxVh) / 100);
  return Math.min(PREVIEW_MAX_HEIGHT_PX, vhCap);
}

function measureIframeContentHeight(doc: Document, capPx: number): number {
  const root = doc.documentElement;
  const body = doc.body;
  const raw = Math.max(
    root.scrollHeight,
    root.offsetHeight,
    body?.scrollHeight ?? 0,
    body?.offsetHeight ?? 0,
    FIT_MIN_PX
  );
  return Math.min(raw, capPx);
}

export function EmailBodyPreview({
  html,
  loading,
  fitHeightToContent = false,
  contentMaxVh = 90,
}: EmailBodyPreviewProps) {
  const inner = stripScriptsFromHtmlForPreview(html.trim() ? html : EMPTY_PLACEHOLDER);
  const srcDoc = buildPreviewSrcDoc(inner);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [contentHeightPx, setContentHeightPx] = useState<number | null>(null);
  const capPx = previewHeightCapPx(contentMaxVh);
  /**
   * До измерения — большая высота (≤ cap), иначе узкий iframe даёт заниженный scrollHeight
   * и модалка остаётся крошечной.
   */
  const provisionalHeightPx = Math.min(Math.floor(window.innerHeight * 0.85), capPx);

  const measure = useCallback(() => {
    if (!fitHeightToContent) return;
    const el = iframeRef.current;
    const doc = el?.contentDocument;
    if (!doc?.documentElement) return;
    const cap = previewHeightCapPx(contentMaxVh);
    setContentHeightPx(measureIframeContentHeight(doc, cap));
  }, [fitHeightToContent, contentMaxVh]);

  useLayoutEffect(() => {
    if (!fitHeightToContent) return;
    setContentHeightPx(null);
  }, [srcDoc, fitHeightToContent]);

  useEffect(() => {
    if (!fitHeightToContent) return;
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [fitHeightToContent, measure]);

  useEffect(() => {
    if (!fitHeightToContent) return;
    let ro: ResizeObserver | null = null;
    const t = window.setTimeout(() => {
      const doc = iframeRef.current?.contentDocument;
      if (!doc?.documentElement) return;
      ro = new ResizeObserver(() => measure());
      ro.observe(doc.documentElement);
      if (doc.body) ro.observe(doc.body);
    }, 0);
    return () => {
      window.clearTimeout(t);
      ro?.disconnect();
    };
  }, [srcDoc, fitHeightToContent, measure]);

  const handleIframeLoad = () => {
    measure();
    if (!fitHeightToContent) return;
    requestAnimationFrame(() => measure());
    setTimeout(measure, 50);
    setTimeout(measure, 250);
  };

  const fitClass = fitHeightToContent ? ' email-body-preview--fit-height' : '';
  const resolvedPx = fitHeightToContent ? (contentHeightPx ?? provisionalHeightPx) : FIT_MIN_PX;
  const blockHeight = fitHeightToContent ? `${resolvedPx}px` : undefined;

  return (
    <div className={`email-body-preview${fitClass}`}>
      <div className="email-body-preview__message">
        <div
          className="email-body-preview__frame-wrap"
          style={blockHeight ? { height: blockHeight, minHeight: `${FIT_MIN_PX}px` } : undefined}
        >
          <iframe
            ref={iframeRef}
            className="email-body-preview__iframe"
            title="Предпросмотр письма, только просмотр"
            sandbox=""
            srcDoc={srcDoc}
            onLoad={handleIframeLoad}
            style={blockHeight ? { height: blockHeight, flex: 'none' } : undefined}
          />
          {loading ? (
            <div
              className="email-body-preview__loading-overlay"
              aria-busy="true"
              aria-label="Загрузка превью письма"
            >
              <span className="email-body-preview__spinner" />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
